// Client-only PostHog wrapper. For server-side captures, import `analyticsServer`
// from '@/lib/analytics-server' instead. Splitting client/server at the module
// boundary lets the bundler trivially exclude posthog-node from client bundles —
// no isServer runtime guard, no dynamic-import tricks.
import type { KnownEvent, EventProperties } from './analytics/events';

type PropsFor<E extends KnownEvent> =
  EventProperties[E] extends Record<string, unknown> ? EventProperties[E] : Record<string, unknown>;

type Traits = Record<string, unknown>;

function clientCapture(event: string, properties?: Record<string, unknown>) {
  void import('posthog-js').then(({ default: posthog }) => {
    posthog.capture(event, properties);
  });
}

function clientIdentify(userId: string, traits?: Traits) {
  void import('posthog-js').then(({ default: posthog }) => {
    posthog.identify(userId, traits);
  });
}

function clientReset() {
  void import('posthog-js').then(({ default: posthog }) => {
    posthog.reset();
  });
}

function clientCaptureException(error: unknown, properties?: Record<string, unknown>) {
  // Via initPostHog rather than the bare singleton: error boundaries can fire
  // before app/providers.tsx has mounted, and a capture on an uninitialised
  // client is silently dropped.
  void import('./posthog-browser').then(({ initPostHog }) => {
    initPostHog().captureException(error, properties);
  });
}

export const analytics = {
  /** Capture a typed, registered event from the browser. */
  capture<E extends KnownEvent>(event: E, properties?: PropsFor<E>): void {
    clientCapture(event, properties as Record<string, unknown> | undefined);
  },

  /**
   * Report a caught exception to PostHog error tracking as `$exception`.
   * Unlike `capture`, this takes a free-form Error rather than a registered
   * event: the interesting dimension is the stack, not the name.
   */
  captureException(error: unknown, properties?: Record<string, unknown>): void {
    clientCaptureException(error, properties);
  },

  identify(userId: string, traits?: Traits): void {
    clientIdentify(userId, traits);
  },

  reset(): void {
    clientReset();
  },
};

export type { KnownEvent, EventProperties, TrackingProp } from './analytics/events';
